import React, { useState, useEffect, useRef } from 'react';

/**
 * ContextMenu
 *   - x, y : coordonnées (pour positionner le menu)
 *   - options : tableau d'objets { label, onClick, subMenu?: [ ... ] }
 *   - onClose : fonction pour fermer tout le menu
 *   - useFixed : booléen => si true, position: 'fixed' ; sinon 'absolute' 
*/
export default function ContextMenu({ x, y, options, onClose, useFixed = false }) {
  // Indice du sous-menu ouvert (un seul à la fois)
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(null);

  // Timer de fermeture si on quitte le menu global
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Référence au conteneur principal
  const menuRef = useRef(null);

  // Fermer si on clique n'importe où en dehors
  useEffect(() => {
    function handleDocumentClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [onClose]);

  /**
   * Annule un timer de fermeture en cours
   */
  function clearHoverTimeout() {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }

  /**
   * Lance un timer de 0,5 s pour fermer tout sous-menu 
   * (quand la souris quitte entièrement le menu)
   */
  function closeAllWithDelay() {
    const timer = setTimeout(() => {
      setActiveSubMenuIndex(null);
    }, 500);
    setHoverTimeout(timer);
  }

  /**
   * Ouvrir immédiatement un sous-menu (ferme l’ancien si besoin)
   */
  function openSubMenu(index) {
    clearHoverTimeout();
    setActiveSubMenuIndex(index);
  }

  /**
   * Griser (hover) un item principal au survol
   */
  function handleItemMouseEnter(e) {
    e.currentTarget.style.backgroundColor = '#eee';
  }

  /**
   * Dé-griser un item principal s'il n'a pas de sous-menu actif
   */
  function handleItemMouseLeave(e, isSubMenuOpen) {
    if (!isSubMenuOpen) {
      e.currentTarget.style.backgroundColor = 'transparent';
    }
    // Si le sous-menu est ouvert, on le laisse en gris
  }

  return (
    <div
      ref={menuRef}
      style={{
        position: useFixed ? 'fixed' : 'absolute',
        top: y,
        left: x,
        backgroundColor: '#f9f9f9',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '4px 0',
        zIndex: 1000,
        // Limiter la hauteur du menu principal et activer le défilement vertical
        maxHeight: 'calc(100vh - 10px)',
        //overflowY: 'auto',
      }}
      onMouseEnter={clearHoverTimeout} // On reste dans le menu => annule la fermeture
      onMouseLeave={closeAllWithDelay}   // On sort du menu => ferme tout après 0,5 s
    >
      {(options || []).map((option, index) => {
        const hasSubMenu = Array.isArray(option?.subMenu) && option.subMenu.length > 0;
        const isSubMenuOpen = activeSubMenuIndex === index;
  
        return (
          <div
            key={index}
            style={{ position: 'relative' }}
            onMouseEnter={(e) => {
              handleItemMouseEnter(e);
              // Si l'option a un sous-menu, on l’ouvre immédiatement
              if (hasSubMenu) {
                openSubMenu(index);
              }
            }}
            onMouseLeave={(e) => {
              handleItemMouseLeave(e, isSubMenuOpen);
            }}
          >
            {/* ITEM PRINCIPAL */}
            <div
              style={{
                padding: '8px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                backgroundColor: isSubMenuOpen ? '#eee' : 'transparent',
              }}
              onClick={() => {
                // Si pas de sous-menu => exécute l'action et ferme
                if (!hasSubMenu) {
                  option.onClick?.();
                  onClose();
                }
              }}
            >
              {option.label}
              {hasSubMenu && <span style={{ float: 'right' }}>▶</span>}
            </div>
  
            {/* Sous-menu si ouvert */}
            {hasSubMenu && isSubMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '100%',
                  marginLeft: '0px', // pas de gap
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  padding: '4px 0',
                  zIndex: 1001,
                  // Modification : permettre l'affichage complet du sous-menu
                  maxHeight: 'calc(100vh - 10px)',
                  overflowY: 'visible',
                }}
              >
                {(option.subMenu || []).map((subOption, subIndex) => (
                  <div
                    key={subIndex}
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      backgroundColor: 'transparent',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#eee';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => {
                      subOption.onClick?.();
                      onClose();
                    }}
                  >
                    {subOption.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
  
  
  
}
